<template>
  <div>
    <div v-for="field in schema" :key="field.name">
      <v-row dense>
        <v-col sm="12" md="10" offset-md="1">
          <component
            :is="getComponent(field)"
            :name="field.name"
            :label="field.label || field.name"
            :config="field"
            :type="field.type"
            :initialValue="values[field.name] || null"
            :resolved="resolvedData(field.name)"
            @change="modifyRecordField(field, $event)"
            v-if="showFields[field.name]"
            :disabled="isDisabled(field)"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import LeanExternalMedia from '@/components/fields/LeanExternalMedia';
import LeanTextArea from '@/components/fields/LeanTextArea';
import LeanString from '@/components/fields/LeanString';
import LeanBoolean from '@/components/fields/LeanBoolean';
import LeanImage from '@/components/fields/LeanImage';
import LeanVideo from '@/components/fields/LeanVideo';
import LeanLinkedDocument from '@/components/fields/LeanLinkedDocument';
import LeanEmbeddedDocument from '@/components/fields/LeanEmbeddedDocument';
import LeanEmbeddedDocuments from '@/components/fields/LeanEmbeddedDocuments';
import LeanLinkedDocuments from '@/components/fields/LeanLinkedDocuments';
import LeanAudio from './fields/LeanAudio.vue';
import LeanFile from './fields/LeanFile.vue';
import LeanEnum from '@/components/fields/LeanEnum';
import LeanDate from '@/components/fields/LeanDate';
import LeanMarkdown from '@/components/fields/LeanMarkdown';
import ViluaRecipe from '@/components/fields/ViluaRecipe';

export default {
  name: 'LeanFormBlock',
  components: {
    LeanExternalMedia,
    LeanTextArea,
    LeanString,
    LeanBoolean,
    LeanImage,
    LeanVideo,
    LeanLinkedDocument,
    LeanLinkedDocuments,
    LeanEmbeddedDocument,
    LeanEmbeddedDocuments,
    LeanAudio,
    LeanFile,
    LeanEnum,
    LeanDate,
    LeanMarkdown,
    ViluaRecipe
  },
  props: {
    initialValues: {}, // allow nulls for "create" forms
    resolved: {},
    schema: {
      type: Array,
      required: true
    }
  },
  computed: {
    ...mapState({
      selectedLanguage: 'selectedLanguage'
    })
  },
  data() {
    return {
      newAttachments: {},
      isModified: false,
      values: JSON.parse(JSON.stringify(this.initialValues)),
      showFields: {}
    };
  },
  mounted() {
    this.checkConditions();
  },
  methods: {
    async modifyRecordField(field, value) {
      console.log('LeanFormBlock::modifyRecordField', field, value);
      // TODO: define globally which fields are attachment fields
      if (['image', 'video', 'audio', 'attachment'].indexOf(field.type) !== -1) {
        this.values[field.name] = value;

        // An attached file is added
        if (this.initialValues[field.name] !== value && value !== null) {
          this.newAttachments[field.name] = {
            field: field,
            path: `${this.selectedLanguage}/${field.name}/`,
            value: value.data,
            file: value.file
          };
        }
        // An attached file was removed before uploading
        if (this.initialValues[field.name] !== value && this.newAttachments[field.name] !== null && value === null) {
          delete this.newAttachments[field.name];
        }
      } else {
        this.values[field.name] = value;
      }
      console.log('emitting newattachments', this.newAttachments);
      this.isModified = true;
      this.$emit('change', {
        fieldName: field.name,
        newAttachments: this.newAttachments,
        values: this.values
      });
      this.checkConditions();
    },
    enabledViaCondition(field) {
      if (!field.conditions) {
        return true;
      }

      return !field.conditions.some((condition) => {
        if (condition.type === 'one-of') {
          const sourceValue = this.values?.[condition.source];
          if (sourceValue && condition.values.includes(sourceValue)) {
            return false;
          }
          this.values[field.name] = null;
          return true;
        }

        if (condition.type === 'exist') {
          const fieldValue = this.values?.[condition.source];
          return (
            condition.exists ===
            (fieldValue === null ||
              fieldValue === undefined ||
              (Array.isArray(fieldValue) && fieldValue.length === 0) ||
              (typeof fieldValue === 'string' && fieldValue.length === 0) ||
              !!(Object.keys(fieldValue)?.length === 0))
          );
        }
      });
    },
    checkConditions() {
      this.showFields = {};
      for (const field of this.schema) {
        this.showFields[field.name] = this.enabledViaCondition(field);
      }
    },
    resolvedData(fieldName) {
      return !!this.resolved && this.resolved[fieldName] ? this.resolved[fieldName] : null;
    },
    isDisabled(field){
      return field?.disabled;
    },
    getComponent(field) {
      if (field.type === 'string' || field.type === 'number') {
        return 'LeanString';
      } else if (field.type === 'date' || field.type === 'datetime' || field.type === 'time') {
        return 'LeanDate';
      } else if (field.type === 'enum') {
        return 'LeanEnum';
      } else if (field.type === 'boolean') {
        return 'LeanBoolean';
      } else if (field.type === 'html') {
        return 'LeanTextArea';
      } else if (field.type === 'attachment') {
        return 'LeanFile';
      } else if (field.type === 'image') {
        return 'LeanImage';
      } else if (field.type === 'video') {
        return 'LeanVideo';
      } else if (field.type === 'audio') {
        return 'LeanAudio';
      } else if (field.type === 'url') {
        return 'LeanExternalMedia';
      } else if (field.type === 'document' && field.linked === true) {
        return 'LeanLinkedDocument';
      } else if (field.type === 'document[]' && field.linked === true) {
        return 'LeanLinkedDocuments';
      } else if (field.type === 'document') {
        return 'LeanEmbeddedDocument';
      } else if (field.type === 'document[]') {
        return 'LeanEmbeddedDocuments';
      } else if (field.type === 'markdown') {
        return 'LeanMarkdown';
      } else if (field.type === 'vilua-recpie') {
        return 'ViluaRecipe';
      } else {
        return 'LeanString';
      }
    }
  }
};
</script>

<style scoped></style>
